import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Toast } from '@capacitor/toast';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { App } from '@capacitor/app';
import { isNativeApp } from '../utils/platformUtils';

export class NativePluginsService {
  /**
   * Take a photo using the device camera
   */
  static async takePhoto(): Promise<string | null> {
    if (!isNativeApp()) {
      // Fallback for web - could implement file input or return null
      console.warn('Camera not available on web platform');
      return null;
    }

    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
      });

      return image.webPath || null;
    } catch (error) {
      console.error('Error taking photo:', error);
      await this.showToast('Failed to take photo');
      return null;
    }
  }

  /**
   * Select a photo from the device gallery
   */
  static async selectPhoto(): Promise<string | null> {
    if (!isNativeApp()) {
      // Fallback for web - could implement file input or return null
      console.warn('Gallery not available on web platform');
      return null;
    }

    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
      });

      return image.webPath || null;
    } catch (error) {
      console.error('Error selecting photo:', error);
      await this.showToast('Failed to select photo');
      return null;
    }
  }

  /**
   * Save data to a file on the device
   */
  static async saveFile(data: string, filename: string, directory: Directory = Directory.Documents): Promise<boolean> {
    if (!isNativeApp()) {
      // Fallback for web - download file
      this.downloadFile(data, filename);
      return true;
    }

    try {
      await Filesystem.writeFile({
        path: filename,
        data: data,
        directory: directory,
        encoding: Encoding.UTF8,
      });
      await this.showToast('File saved successfully');
      return true;
    } catch (error) {
      console.error('Error saving file:', error);
      await this.showToast('Failed to save file');
      return false;
    }
  }

  /**
   * Read a file from the device
   */
  static async readFile(filepath: string, directory: Directory = Directory.Documents): Promise<string | null> {
    if (!isNativeApp()) {
      console.warn('File system not available on web platform');
      return null;
    }

    try {
      const result = await Filesystem.readFile({
        path: filepath,
        directory: directory,
        encoding: Encoding.UTF8,
      });
      return result.data;
    } catch (error) {
      console.error('Error reading file:', error);
      await this.showToast('Failed to read file');
      return null;
    }
  }

  /**
   * Share content using native sharing
   */
  static async shareContent(title: string, text: string, url?: string): Promise<boolean> {
    if (!isNativeApp()) {
      // Fallback for web - use Web Share API if available
      if (navigator.share) {
        try {
          await navigator.share({ title, text, url });
          return true;
        } catch (error) {
          console.warn('Web share failed:', error);
        }
      }
      // Fallback to copying to clipboard
      await navigator.clipboard.writeText(`${title}\n${text}${url ? `\n${url}` : ''}`);
      await this.showToast('Content copied to clipboard');
      return true;
    }

    try {
      await Share.share({
        title,
        text,
        url,
      });
      return true;
    } catch (error) {
      console.error('Error sharing content:', error);
      await this.showToast('Failed to share content');
      return false;
    }
  }

  /**
   * Show a toast message
   */
  static async showToast(message: string, duration: 'short' | 'long' = 'short'): Promise<void> {
    if (!isNativeApp()) {
      // Fallback for web - could use a toast library or console
      console.log('Toast:', message);
      return;
    }

    try {
      await Toast.show({
        text: message,
        duration: duration === 'short' ? 'short' : 'long',
      });
    } catch (error) {
      console.error('Error showing toast:', error);
    }
  }

  /**
   * Trigger haptic feedback
   */
  static async triggerHaptic(style: ImpactStyle = ImpactStyle.Medium): Promise<void> {
    if (!isNativeApp()) {
      return; // No haptic feedback on web
    }

    try {
      await Haptics.impact({ style });
    } catch (error) {
      console.error('Error triggering haptic:', error);
    }
  }

  /**
   * Get app state information
   */
  static async getAppState(): Promise<{ isActive: boolean } | null> {
    if (!isNativeApp()) {
      return { isActive: document.hasFocus() };
    }

    try {
      const state = await App.getState();
      return { isActive: state.isActive };
    } catch (error) {
      console.error('Error getting app state:', error);
      return null;
    }
  }

  /**
   * Listen to app state changes
   */
  static addAppStateListener(callback: (state: { isActive: boolean }) => void): () => void {
    if (!isNativeApp()) {
      // Fallback for web
      const handleFocus = () => callback({ isActive: true });
      const handleBlur = () => callback({ isActive: false });
      
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);
      
      return () => {
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
      };
    }

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      callback({ isActive });
    });

    return () => listener.remove();
  }

  /**
   * Web fallback: Download file
   */
  private static downloadFile(data: string, filename: string): void {
    const blob = new Blob([data], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
